import React, { useEffect, useMemo, useRef, useState } from 'react'

import styles from "./styles/Game.module.scss"

import { Container, Form, Spinner } from 'react-bootstrap'
import { Button, Description, DescriptionLight, Header, Headline, Input } from '../../Components/CustomComponent'

import moment from 'moment'
import { IoCheckmarkCircleSharp } from 'react-icons/io5'
import { RxCross1 } from 'react-icons/rx'
import { useMutation } from 'react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { QuizQuestionAPI } from '../../Api/QuizQuestions'
import TimerNavbar from '../../Components/Layouts/TimerNavbar'
import { useAppContext } from '../../Context/AppContext'
import { useSocket } from '../../Context/SocketContext'
import { baseRoute } from '../../Helper'
import { Toast } from '../../Hooks/useToast'
import { BUZZER_PRESS_TIMER, OPTIONS_TYPES, QUESTION_TYPES, STRING, WEB_ROUTES } from '../../constants'
import AudienceSupport from './components/AudienceSupport'
import GoogleSupport from './components/GoogleSupport'
import ShareSupport from './components/ShareSupport'
import { Controller, useForm } from 'react-hook-form'


export default function Game({ data }) {
    const navigate = useNavigate()
    const quizQuestionAPI = QuizQuestionAPI()
    const socket = useSocket()
    const [searchParams, setSearchParams] = useSearchParams()
    const { isSocketConnected, user: { id: user_id } } = useAppContext();

    const clockSoundRef = useRef(null);
    const rightAnswerSoundRef = useRef(null);
    const wrongAnswerSoundRef = useRef(null);
    const buzzerSoundRef = useRef(null);


    const answerTimerRef = useRef()

    const [currentQuestion, setCurrentQuestion] = useState(() => findQuestionById(data.active_question_id))
    const [timer, setTimer] = useState(() => {
        return getTimer(data)
    })

    const INITIAL_STATE = useMemo(() => {
        const isMyTurn = data?.buzzer_user?.id === user_id
        return {
            timerDifference: 0,
            coins: data?.coins || 0,
            isBuzzerDecision: false,
            isBuzzerSubmitted: false,
            isBuzzerDisabled: !!data.is_buzzer,
            buzzer_user: data?.buzzer_user || {},
            isMyTurn: isMyTurn,
            //Handle Audience Support 
            isAudienceSupportRequesting: false,
            isAudienceSupportRequested: false,
            isAudienceSupport: (!!isMyTurn && !!data.is_active_audience_poll),
            //Handle Share Support
            isShareSupportRequesting: false,
            isShareSupportRequested: false,
            isShareSupport: (!!isMyTurn && !!data.is_active_expert_help),
            //Handle Google Support
            isGoogleSupportRequesting: false,
            isGoogleSupportRequested: false,
            isGoogleSupport: (!!isMyTurn && !!data.is_active_google_help),
            googleDataHandler: {},
            //Handle Reply
            isAlreadyReplied: !!data.is_answered,
            isReplying: false,
            replyId: undefined,
            isReplied: false,
            isCorrectReply: false,
            replyAnswer: undefined,
            replyAnswerData: {}
        }
    }, [])
    const [state, setState] = useState(INITIAL_STATE)


    const { handleSubmit, control, setValue, watch } = useForm({});
    const userAnswer = watch('answer')

    const quizQuestionMutation = useMutation(quizQuestionAPI.submitQuizAnswer, {
        onSuccess: (data) => {
            const isCorrect = !!data?.data?.is_correct;
            const timeout = 3;
            clockSoundRef.current.pause();

            setState(prev => {
                return {
                    ...prev,
                    timerDifference: timeout + 1,
                    coins: isCorrect ? prev.coins + (currentQuestion?.points || 0) : prev.coins,
                    isReplying: false,
                    replyId: undefined,
                    isReplied: true,
                    isCorrectReply: isCorrect,
                    replyAnswerData: {
                        ...data?.data,
                        question_type: currentQuestion.question_type,
                        option: currentQuestion?.question_options?.find(item => item.id === data.data.answer)
                    }
                }
            })

            if (!isCorrect) {
                wrongAnswerSoundRef.currentTime = 0
                wrongAnswerSoundRef.current.play()
                answerTimerRef.current = setTimeout(() => {
                    setState(prev => {
                        return {
                            ...prev,
                            isReplying: false,
                            isAlreadyReplied: true,
                            isReplied: false,
                            replyId: undefined,
                        }
                    })

                }, timeout * 1000)
            }
            else {
                rightAnswerSoundRef.current.currentTime = 0;
                rightAnswerSoundRef.current.play();
            }

            setValue('answer', null)
        },
        onError: (err) => {
            Toast(err.message || STRING.server_error, 'error', false)
            setState(prev => {
                return {
                    ...prev,
                    isReplying: false,
                    replyId: undefined
                }
            })
        }
    })



    const buzzerPressMutation = useMutation(quizQuestionAPI.pressBuzzer, {
        onSuccess: (data) => {
            try {
                buzzerSoundRef.current?.play();
            }
            catch (err) { }

            setState(prev => {
                return {
                    ...prev,
                    isBuzzerSubmitted: false,
                }
            })
        },
        onError: (err) => {
            let isDecison = false;
            if (err.code !== 422) {
                Toast(err.message || STRING.server_error, 'error', false)
            }
            else {
                isDecison = true;
            }
            setState(prev => {
                return {
                    ...prev,
                    isBuzzerDecision: prev.isBuzzerDisabled ? false : isDecison,
                    isBuzzerSubmitted: false,
                }
            })
        }
    })

    useEffect(() => {
        let audio;

        clockSoundRef.current = new Audio('/assets/sounds/clock.mp3');
        clockSoundRef.current.loop = true;

        rightAnswerSoundRef.current = new Audio('/assets/sounds/right_answer.mp3');

        wrongAnswerSoundRef.current = new Audio('/assets/sounds/wrong_answer.mp3');

        buzzerSoundRef.current = new Audio("/assets/sounds/buzzer.mp3");;

        try {
            audio = new Audio("/assets/sounds/quiz_game_sound.mp3"); // Path to your sound file
            audio.loop = true;
            audio?.play();
        }
        catch (err) { }
        return () => {
            clockSoundRef.current.pause();
            clockSoundRef.current.src = '';

            rightAnswerSoundRef.current.pause();
            rightAnswerSoundRef.current.src = '';

            wrongAnswerSoundRef.current.pause();
            wrongAnswerSoundRef.current.src = '';

            buzzerSoundRef.current.pause();
            buzzerSoundRef.current.src = '';

            if (audio) {
                audio.pause();
                audio.currentTime = 0;
            }
            if (answerTimerRef.current) {
                clearTimeout(answerTimerRef.current)
            }
        }
    }, [])

    useEffect(() => {
        if (isSocketConnected) {

            socket.on('_buzzerPressed', (data) => {
                setState(prev => {
                    return {
                        ...prev,
                        isBuzzerDecision: false,
                        isBuzzerDisabled: true,
                        isMyTurn: data?.buzzer_user?.id === user_id,
                        buzzer_user: data.buzzer_user || {}
                    }
                })
                setTimer(getTimer(data))
                clockSoundRef.current.currentTime = 0;
                clockSoundRef.current.play();

            })

            socket.on('audiencePollRequest_', (data) => {
                setState(prev => {
                    return {
                        ...prev,
                        isAudienceSupportRequesting: false,
                        isAudienceSupportRequested: true,
                        isAudienceSupport: true
                    }
                })
                setTimer(data.data.audience_poll_duration)

            })

            socket.on('expertHelpRequest_', (data) => {
                setState(prev => {
                    return {
                        ...prev,
                        isShareSupportRequesting: false,
                        isShareSupportRequested: true,
                        isShareSupport: true
                    }
                })
                setTimer(data.data.expert_help_duration)
            })

            socket.on('googleHelpRequest_', (data) => {
                setState(prev => {
                    return {
                        ...prev,
                        isGoogleSupportRequesting: false,
                        isGoogleSupportRequested: true,
                        isGoogleSupport: true,
                        googleDataHandler: data.data
                    }
                })
                setTimer(data.data.google_help_duration)

            })



            socket.on('_pollTimeOver', (data) => {
                setState(prev => {
                    return {
                        ...prev,
                        isAudienceSupportRequesting: false,
                        isAudienceSupportRequested: true,
                        isAudienceSupport: false,
                    }
                })
                setTimer(getTimer(data))
            })

            socket.on('_expertHelpTimeOver', (data) => {
                setState(prev => {
                    return {
                        ...prev,
                        isShareSupportRequesting: false,
                        isShareSupportRequested: true,
                        isShareSupport: false
                    }
                })
                setTimer(getTimer(data))
            })



            socket.on('_googleHelpTimeOver', (data) => {
                setState(prev => {
                    return {
                        ...prev,
                        isGoogleSupportRequesting: false,
                        isGoogleSupportRequested: true,
                        isGoogleSupport: false,
                        googleDataHandler: {}
                    }
                })
                if (window?.FlutterChannel) {
                    window?.FlutterChannel?.postMessage("closeModal");
                }
                setTimer(getTimer(data))
            })

            socket.on('_buzzerReleased', (data) => {
                setState(prev => {
                    return {
                        ...prev,
                        timerDifference: 0,
                        isBuzzerDisabled: false,
                        buzzer_user: {},
                        isMyTurn: false,
                        isAlreadyReplied: (prev.buzzer_user.id === user_id) || prev.isAlreadyReplied
                    }
                })
                setTimer(BUZZER_PRESS_TIMER)
                clockSoundRef.current.pause();

            })

            socket.on('_moveNext', (data) => {
                const nextQuestion = findQuestionById(data?.nextQuestion?.id)
                setCurrentQuestion(nextQuestion);
                setState(prev => {
                    return {
                        ...prev,
                        ...INITIAL_STATE,
                        coins: prev.coins,
                        buzzer_user: {},
                        isBuzzerDisabled: false,
                        isAudienceSupport: false,
                        isShareSupport: false,
                        isGoogleSupport: false,
                        isAlreadyReplied: false,
                    }
                })
                setTimer(BUZZER_PRESS_TIMER)
            })

            socket.on('error', (data) => {
            })

        }

        return () => {
            socket.dispose('_buzzerPressed')
            socket.dispose('audiencePollRequest_')
            socket.dispose('expertHelpRequest_')
            socket.dispose('googleHelpRequest_')
            socket.dispose('_pollTimeOver')
            socket.dispose('_expertHelpTimeOver')
            socket.dispose('_googleHelpTimeOver')
            socket.dispose('_buzzerReleased')
            socket.dispose('_moveNext')
            socket.dispose('error')
        }
    }, [isSocketConnected])



    useEffect(() => {
        if (!isSocketConnected) return;
        socket.emit('_typing', {
            quiz_id: data.id,
            question_id: currentQuestion.id,
            typing: userAnswer
        })

    }, [isSocketConnected, userAnswer, currentQuestion])



    function getTimer(payload) {
        if (!payload.is_buzzer) {
            const duration = moment.duration(moment(moment(payload.active_at).add(BUZZER_PRESS_TIMER, 'seconds')).diff(moment()))
            return duration.asSeconds()
        }

        const isMyTurn = payload?.buzzer_user?.id === user_id;
        const isAudienceSupport = payload?.is_active_audience_poll;
        const isShareSupport = payload?.is_active_expert_help;
        const isGoogleSupport = payload?.is_active_google_help;

        if (isMyTurn && isAudienceSupport) {
            const duration = moment.duration(moment(moment(payload.active_at).add(payload.audience_poll_duration, 'seconds')).diff(moment()))
            return duration.asSeconds()
        }

        if (isMyTurn && isShareSupport) {
            const duration = moment.duration(moment(moment(payload.active_at).add(payload.expert_help_duration, 'seconds')).diff(moment()))
            return duration.asSeconds()
        }

        if (isMyTurn && isGoogleSupport) {
            const duration = moment.duration(moment(moment(payload.active_at).add(payload.google_help_duration, 'seconds')).diff(moment()))
            return duration.asSeconds()
        }


        const activeAt = moment(payload.active_at);
        const durationMs = moment(activeAt).add(currentQuestion.duration, 'seconds').diff(moment());
        const duration = moment.duration(durationMs);
        return duration.asSeconds()
    }

    function findQuestionById(question_id) {
        if (!question_id && !!data?.quiz_questions.length) return data?.quiz_questions[0]

        return data.quiz_questions.find(item => item.id === question_id)
    }


    const handleClose = () => {
        navigate(-1, { replace: true })
    }

    const handleBuzzerClick = () => {
        if (state.isBuzzerDisabled || state.isBuzzerSubmitted) return

        setState(prev => {
            return {
                ...prev,
                isBuzzerSubmitted: true,
            }
        })

        const payload = {}
        payload.quiz_id = data.id
        payload.question_id = currentQuestion.id
        buzzerPressMutation.mutate(payload)

    }

    const handleAudienceSupportClick = () => {
        if (!state.isBuzzerDisabled || !state.isMyTurn) return;
        if (state.isAudienceSupport || state.isAudienceSupportRequesting || state.isAudienceSupportRequested) return;
        if (state.isShareSupport || state.isShareSupportRequesting) return;
        if (state.isGoogleSupport || state.isGoogleSupportRequesting) return;

        setState(prev => {
            return {
                ...prev,
                isAudienceSupportRequesting: true,
            }
        })
        socket.emit('_audiencePollRequest', {
            quiz_id: data.id,
            question_id: currentQuestion.id
        })
    }

    const handleShareSupportClick = () => {
        if (!state.isBuzzerDisabled || !state.isMyTurn) return;
        if (state.isAudienceSupport || state.isAudienceSupportRequesting) return;
        if (state.isShareSupport || state.isShareSupportRequesting || state.isShareSupportRequested) return;
        if (state.isGoogleSupport || state.isGoogleSupportRequesting) return;

        setState(prev => {
            return {
                ...prev,
                isShareSupportRequesting: true,
            }
        })
        socket.emit('_expertHelpRequest', {
            quiz_id: data.id,
            question_id: currentQuestion.id
        })
    }

    const handleGoogleSupportClick = () => {
        if (!state.isBuzzerDisabled || !state.isMyTurn) return;
        if (state.isAudienceSupport || state.isAudienceSupportRequesting) return;
        if (state.isShareSupport || state.isShareSupportRequesting) return;
        if (state.isGoogleSupport || state.isGoogleSupportRequesting || state.isGoogleSupportRequested) return;

        setState(prev => {
            return {
                ...prev,
                isGoogleSupportRequesting: true,
            }
        })
        socket.emit('_googleHelpRequest', {
            quiz_id: data.id,
            question_id: currentQuestion.id
        })
    }

    const handleAnswerClick = (answer_id) => {
        if (!state.isMyTurn || !!state.isReplied) return;
        setState(prev => {
            return {
                ...prev,
                isReplying: true,
                replyId: answer_id
            }
        })

        if (currentQuestion.question_type === QUESTION_TYPES.MCQ) {
            socket.emit('_optionSelected', {
                quiz_id: data.id,
                question_id: currentQuestion.id,
                option_id: answer_id
            })
        }

        const payload = {}
        payload.user_quiz_id = data.audience_id;
        payload.question_id = currentQuestion.id;
        payload.answer = answer_id
        quizQuestionMutation.mutate(payload)
        return;
    }

    const onAnswerSubmit = (data) => {
        handleAnswerClick(data.answer)
        return;
    }

    // const handleNextClick = () => {
    //     if (!state.isMyTurn || !state.isReplied) return;
    //     socket.emit('_moveToNextQuestion', {
    //         quiz_id: data.id
    //     })
    // }


    return (
        <Container className={styles.Game} key={currentQuestion?.id}>
            <img src="/assets/quiz-cover.jpg" alt="Quiz Cover" className={styles.quizCover} />

            <div className={styles.contentContainer}>

                <TimerNavbar
                    key={currentQuestion.id + '-' + timer}
                    isTimer={!state.isReplied && !(!state.isMyTurn && !!state.isBuzzerDisabled)}
                    // isTimer={true}
                    timerSeconds={timer - state.timerDifference}
                    coins={state.coins}
                    onClose={handleClose}
                />
                {
                    (state.isReplied || !state.isMyTurn) ? "" :
                        <div className={styles.optionContainer}>
                            <span
                                className={`${styles.option} ${styles.bg} ${state.isShareSupport ? styles.active : ""}`}
                                onClick={handleShareSupportClick}
                            >
                                {state.isShareSupportRequesting ? <Spinner size="sm" color='white' /> :
                                    <img
                                        src={'/assets/share.png'}
                                        alt="Share Icon"
                                        className={styles.image}
                                    />
                                }
                            </span>
                            <span
                                className={`${styles.option}  ${state.isGoogleSupport ? styles.active : ""}`}
                                onClick={handleGoogleSupportClick}
                            >
                                {state.isGoogleSupportRequesting ? <Spinner size="sm" color='white' /> :
                                    <img
                                        src={'/assets/google-search.png'}
                                        alt="Google Search Icon"
                                        className={styles.image}
                                    />
                                }
                            </span>
                            {currentQuestion.question_type !== QUESTION_TYPES.MCQ ? "" :
                                <span
                                    className={`${styles.option} ${styles.bg} ${state.isAudienceSupport ? styles.active : ""}`}
                                    onClick={handleAudienceSupportClick}
                                >
                                    {state.isAudienceSupportRequesting ? <Spinner size="sm" color='white' /> :
                                        <img
                                            src={'/assets/audience-icon.png'}
                                            alt="Audience Help Icon"
                                            className={styles.image}
                                        />
                                    }
                                </span>
                            }
                        </div>
                }
                <div className={styles.coverImage}>
                    <img
                        key={currentQuestion?.id}
                        src={currentQuestion?.image_url || data.cover_image_url}
                        alt="Cover Image"
                        className={styles.image}
                    />
                </div>
                <div className={styles.content}>
                    <DescriptionLight
                        text={`Question ${(currentQuestion?.position || 0)} of ${data.quiz_questions.length}`}
                        size='55'
                    />
                    {
                        !state.isReplied ?
                            <>
                                <Headline
                                    text={currentQuestion?.question}
                                    classes='mt-2 mb-4'
                                />
                                <div key={currentQuestion.id} className={styles.answersContainer}>
                                    {!!state.isAudienceSupport ?
                                        <AudienceSupport
                                            quiz_id={data.id}
                                            question_id={currentQuestion.id}
                                            options={currentQuestion?.question_options}
                                        /> :
                                        !!state.isShareSupport ? <ShareSupport question={currentQuestion} /> :
                                            !!state.isGoogleSupport ? <GoogleSupport data={state.googleDataHandler} question={currentQuestion?.question} /> :
                                                (currentQuestion?.question_type === QUESTION_TYPES.BLANK) ?
                                                    (!!state.isMyTurn || !!state.isBuzzerDecision || !!state.isAlreadyReplied || !!state.isReplied || !!state.isBuzzerDisabled) ? <></>
                                                        : <Description
                                                            classes='my-4 text-center'
                                                            text={"Press buzzer to answer the question"}
                                                            size='6'
                                                        />
                                                    :
                                                    currentQuestion?.question_options?.map((item, ind) => {
                                                        return <Button
                                                            text={
                                                                item?.option_type === OPTIONS_TYPES.TEXT ?
                                                                    item.option :
                                                                    <img src={item.image_url} alt={'Option Image ' + ind} />
                                                            }
                                                            lightTheme={true}
                                                            classes='fw-bold'
                                                            onClick={() => handleAnswerClick(item.id)}
                                                            isSubmitted={state.replyId === item.id}
                                                            disabled={!state.isMyTurn}
                                                        />
                                                    })}
                                </div>
                            </> :
                            <>
                                <Header
                                    text={currentQuestion.question}
                                    classes='mt-2 mb-4'
                                />
                                <Button
                                    text={
                                        state.replyAnswerData?.question_type === QUESTION_TYPES.BLANK ?
                                            state.replyAnswerData?.answer :
                                            state.replyAnswerData?.option?.option_type === OPTIONS_TYPES.TEXT ?
                                                state.replyAnswerData?.option.option :
                                                <img src={state.replyAnswerData.option?.image_url} alt={'Replied Option Image'} />
                                    }
                                    lightTheme={true}
                                    success={state.isCorrectReply ? true : false}
                                    fail={!state.isCorrectReply ? true : false}
                                    prefixIcon={
                                        state.isCorrectReply ?
                                            <IoCheckmarkCircleSharp color='#32BA7C' size={30} /> :
                                            <RxCross1 color='#ba3232' size={20} />
                                    }
                                    classes='fw-bold mt-3 mb-4 p-3'
                                    disabled={true}
                                />
                                <Description
                                    text={
                                        state.isCorrectReply ?
                                            `That’s the right Answer +${state.replyAnswerData.coins} Coins` :
                                            `That’s the wrong Answer !`
                                    }
                                    size='6'
                                    classes='text-center'
                                />
                            </>
                    }

                </div>
                {(!!state.isAudienceSupport || !!state.isShareSupport || !!state.isGoogleSupport) ? "" :
                    !!state.isBuzzerDecision ?
                        <Description
                            text={"Waiting for the decision."}
                            size='6'
                            classes='text-center my-5'
                        />
                        :
                        !!state.isAlreadyReplied ?
                            <Description
                                text={"You’ve already taken your chance."}
                                size='6'
                                classes='text-center my-5'
                            />
                            :
                            (!!state.isMyTurn) ?
                                <>
                                    {state.isReplied || (currentQuestion?.question_type !== QUESTION_TYPES.BLANK) ? "" :
                                        <Form onSubmit={handleSubmit(onAnswerSubmit)} key={currentQuestion.id}>
                                            <Controller
                                                name="answer"
                                                control={control}
                                                rules={{
                                                    required: true
                                                }}
                                                render={({ field }) => (
                                                    <Input
                                                        {...field}
                                                        label="Answer"
                                                        placeholder="Add here"
                                                        labelClasses="fs-7"
                                                    />
                                                )}
                                            />
                                            <Button type='submit' text='Submit Answer' classes='mt-1' isSubmitted={state.isReplying} disabled={state.isReplying} />
                                        </Form>
                                    }
                                    <Description
                                        text={"Your turn to answer the question."}
                                        size='6'
                                        classes='text-center my-5'
                                    />
                                </> :
                                !state.isReplied ?
                                    (state.isBuzzerDisabled && !state.isMyTurn) ?
                                        <div className={styles.buzzerUser}>
                                            <img
                                                src={state.buzzer_user.image_url}
                                                alt='Buzzer User Image'
                                                className={styles.image}
                                            />
                                            <Description
                                                text={state.buzzer_user.name}
                                                classes='my-2'
                                            />
                                            <Description
                                                text={'Waiting for the user to reply'}
                                                size='6'
                                            />

                                        </div>
                                        :
                                        <div className={styles.buzzerContainer}>
                                            <img
                                                src="/assets/buzzer.png"
                                                alt="Buzzer Image"
                                                className={`${styles.buzzerImage}  ${state.isBuzzerDisabled ? styles.disabled : ""}`}
                                                onClick={handleBuzzerClick}
                                            />
                                            {/* <button
                                        className={`${styles.buzzer} ${state.isBuzzerDisabled ? styles.disabled : ""}`}
                                        onClick={handleBuzzerClick}
                                        disabled={state.isBuzzerDisabled}
                                    >
                                        {!state.isBuzzerSubmitted ? "BUZZER" : <Spinner />}
                                    </button> */}
                                        </div>
                                    :
                                    <div className={styles.repliedContainer}>
                                        {
                                            !state.isCorrectReply ? "" :
                                                <div className={styles.coinImage}>
                                                    <img
                                                        src='/assets/coins.png'
                                                        alt='Coin Image'
                                                        className={styles.image}
                                                    />
                                                </div>
                                        }
                                        {/* {(!state.isMyTurn || !state.isCorrectReply) ? "" :
                                <div className={styles.nextContainer}>
                                    <DescriptionLight
                                        text='Next'
                                        size='55'
                                    />
                                    <Button
                                        text={<FaArrowRight size={24} color='#927AFF' />}
                                        classes={styles.nextButton}
                                        lightTheme={true}
                                        onClick={handleNextClick}
                                    />
                                </div>
                            } */}
                                    </div>
                }
            </div>
        </Container>
    )
}
